import {applyTranslations} from '../../i18n/i18n.js';

/**
 * Cross-cutting rules every shadow root should pick up automatically — like the
 * button press-feedback nudge. Constructable stylesheets let the browser parse this
 * once and adopt the same instance into every component's shadow root, so neither
 * developers nor browsers have to deal with the same rule duplicated across N files.
 */
const sharedInteractionStyles = new CSSStyleSheet();
sharedInteractionStyles.replaceSync(`
    .btn:not(:disabled):active {
        transform: translateY(1px);
    }
    @media (prefers-reduced-motion: reduce) {
        .btn { transition: none; }
        .btn:active { transform: none; }
    }
`);

export class BaseComponent extends HTMLElement {
    #localeChangeHandler = null;

    constructor() {
        super();
        this.attachShadow({mode: 'open'});
    }

    /**
     * Loads external HTML template and CSS into Shadow DOM. Also re-attaches the global
     * font and Font Awesome stylesheets into the shadow root — they're loaded once in
     * the light DOM via index.html, but CSS in a shadow tree doesn't inherit from
     * outside, so `<i class="fa-...">` icons and custom font-faces would otherwise
     * render as empty boxes / fallback serif. The browser cache makes the extra
     * <link>s practically free.
     *
     * With `watchLocale: false`, `data-i18n` nodes are translated once and no document
     * listener is registered, for components whose template has none to re-translate.
     */
    loadTemplate(htmlContent, cssContent, {watchLocale = true} = {}) {
        const fontsLink = document.createElement('link');
        fontsLink.rel = 'stylesheet';
        fontsLink.href = 'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600;9..144,700&family=Geist:wght@400;500;600;700&family=Geist+Mono:wght@400;500&display=swap';

        const faLink = document.createElement('link');
        faLink.rel = 'stylesheet';
        faLink.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css';

        this.shadowRoot.innerHTML = `<style>${cssContent}</style>${htmlContent}`;
        this.shadowRoot.prepend(faLink);
        this.shadowRoot.prepend(fontsLink);
        this.shadowRoot.adoptedStyleSheets = [...this.shadowRoot.adoptedStyleSheets, sharedInteractionStyles];
        applyTranslations(this.shadowRoot);
        if (!watchLocale) return;
        this.#localeChangeHandler = () => applyTranslations(this.shadowRoot);
        document.addEventListener('locale-change', this.#localeChangeHandler);
    }

    disconnectedCallback() {
        if (this.#localeChangeHandler) {
            document.removeEventListener('locale-change', this.#localeChangeHandler);
            this.#localeChangeHandler = null;
        }
    }

    $(selector) {
        return this.shadowRoot.querySelector(selector);
    }

    $$(selector) {
        return this.shadowRoot.querySelectorAll(selector);
    }

    emit(name, detail = {}) {
        this.dispatchEvent(new CustomEvent(name, {
            bubbles: true,
            composed: true,
            detail,
        }));
    }
}
